import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { App, Tabs, Modal, Form, Input, Typography, Row, Col, Card } from 'antd'
import PatternNavigationMenu from './PatternNavigationMenu'
import PatternDetailView from './PatternDetailView'
import RenameComponentModal from './RenameComponentModal'
import { useCreateDiscoveryComponentMutation } from '../../../../../store/apis/discovery-component.api'
import { useParams } from 'react-router'
import { COMPONENT_NAME_LIMIT } from '../../../../../constants/input-limits'

const ComponentsTabs = ({
    components = [],
    patterns = [],
    form,
    showPlaceholders,
    criteriaUpdating,
    updateCriteriaFields,
    onRegenerateAndProject,
    selectedPatternIds,
    onPatternSelectChange,
    filters,
    onRemove,
    onRenameComponent,
    onDeleteComponent,
    onPatternMove,
    onActiveChange,
}) => {
    const { message } = App.useApp()
    const { token } = useParams()
    const [activeTabKey, setActiveTabKey] = useState(null)
    const [activePatternId, setActivePatternId] = useState(null)
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
    const [componentToRename, setComponentToRename] = useState(null)
    const [createForm] = Form.useForm()
    const { Text } = Typography
    const [createDiscoveryComponent, { isLoading: isCreatingComponent }] = useCreateDiscoveryComponentMutation()

    const sortedComponents = useMemo(
        () => [...(components || [])].sort((a, b) => a.order - b.order),
        [components]
    )

    const componentIdToPatterns = useMemo(() => {
        const map = new Map()
        sortedComponents.forEach(c => map.set(c.id, []))
        ;(patterns || []).forEach(p => {
            const arr = map.get(p.discovery_component_id) || []
            arr.push(p)
            map.set(p.discovery_component_id, arr)
        })
        return map
    }, [sortedComponents, patterns])

    // Apply filters to patterns
    const getFilteredPatterns = useCallback((componentPatterns) => {
        if (!filters.warningLevels && filters.hasConceptualLabel === null) return componentPatterns

        return componentPatterns.filter(pattern => {
            // Filter by warning level
            if (filters.warningLevels && filters.warningLevels.length > 0) {
                const warningLevel = form.getFieldValue(`warning-level-${pattern.id}`)
                if (!filters.warningLevels.includes(warningLevel)) {
                    return false
                }
            }

            // Filter by conceptual labels
            if (filters.hasConceptualLabel !== null) {
                const conceptualLabels = form.getFieldValue(`cl-${pattern.id}`) || []
                const hasLabels = conceptualLabels.length > 0
                if (hasLabels !== filters.hasConceptualLabel) {
                    return false
                }
            }

            return true
        })
    }, [filters, form])

    useEffect(() => {
        const ids = sortedComponents.map(c => String(c.id))
        if (ids.length === 0) {
            setActiveTabKey(null)
            return
        }
        if (!activeTabKey || !ids.includes(activeTabKey)) {
            setActiveTabKey(ids[0])
            onActiveChange?.(ids[0])
        }
    }, [sortedComponents, activeTabKey, onActiveChange])

    const handleTabChange = useCallback((newActiveKey) => {
        setActiveTabKey(newActiveKey)
        onActiveChange?.(newActiveKey)

        // Set the first pattern of the new component as active
        const componentPatterns = componentIdToPatterns.get(Number(newActiveKey)) || []
        const filteredPatterns = getFilteredPatterns(componentPatterns)
        if (filteredPatterns.length > 0) {
            setActivePatternId(filteredPatterns[0].id)
        } else {
            setActivePatternId(null)
        }
    }, [onActiveChange, componentIdToPatterns, getFilteredPatterns])

    const handlePatternClick = useCallback((patternId) => {
        setActivePatternId(patternId)
    }, [])

    const handleRemovePatternAndUpdateActive = useCallback((patternId) => {
        // If deleting the active pattern, select the next or previous one
        if (activePatternId === patternId && activeTabKey) {
            const componentPatterns = componentIdToPatterns.get(Number(activeTabKey)) || []
            const filteredPatterns = getFilteredPatterns(componentPatterns)
            const currentIndex = filteredPatterns.findIndex(p => p.id === patternId)
            const nextPattern = filteredPatterns[currentIndex + 1] || filteredPatterns[currentIndex - 1]
            setActivePatternId(nextPattern?.id || null)
        }
        onRemove(patternId)
    }, [activePatternId, activeTabKey, componentIdToPatterns, getFilteredPatterns, onRemove])

    // Set initial active pattern when component changes or patterns load
    useEffect(() => {
        if (activeTabKey) {
            const componentPatterns = componentIdToPatterns.get(Number(activeTabKey)) || []
            const filteredPatterns = getFilteredPatterns(componentPatterns)

            // If no pattern is active, or active pattern is not in current component, set first pattern
            const isActivePatternInComponent = filteredPatterns.some(p => p.id === activePatternId)
            if (!isActivePatternInComponent && filteredPatterns.length > 0) {
                setActivePatternId(filteredPatterns[0].id)
            }
        }
    }, [activeTabKey, componentIdToPatterns, getFilteredPatterns, activePatternId])

    const handleTabEdit = useCallback(async (targetKey, action) => {
        if (action === 'add') {
            setIsCreateModalOpen(true)
            return
        }

        const idx = sortedComponents.findIndex(c => String(c.id) === String(targetKey))
        if (sortedComponents.length > 1 && String(activeTabKey) === String(targetKey)) {
            const prev = sortedComponents[idx - 1]
            const next = sortedComponents[idx + 1]
            const nextKey = prev ? String(prev.id) : (next ? String(next.id) : null)
            setActiveTabKey(nextKey)
            if (nextKey) onActiveChange?.(nextKey)
        }

        await onDeleteComponent(Number(targetKey))
    }, [sortedComponents, activeTabKey, onDeleteComponent, onActiveChange])

    const handleCreateComponent = useCallback(async (values) => {
        const componentName = values.componentName.trim()
        const isDuplicate = sortedComponents.some(
            c => c.name.toLowerCase() === componentName.toLowerCase()
        )
        if (isDuplicate) {
            message.error('A component with this name already exists. Please choose a different name.')
            return
        }
        try {
            const order = sortedComponents.length || 0
            const newComponent = await createDiscoveryComponent({ token, name: componentName, order }).unwrap()
            setIsCreateModalOpen(false)
            createForm.resetFields()
            if (newComponent?.id != null) setActiveTabKey(String(newComponent.id))
            message.success('Component created successfully!')
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error creating component:', error)
            message.error('Failed to create component. Please try again.')
        }
    }, [createDiscoveryComponent, sortedComponents, token, createForm])

    const handleOpenRenameModal = useCallback((component, e) => {
        e.stopPropagation()
        setComponentToRename(component)
    }, [])

    const handleCloseRenameModal = useCallback(() => {
        setComponentToRename(null)
    }, [])

    return (
        <>
            <Tabs
                type="editable-card"
                hideAdd={false}
                activeKey={activeTabKey || undefined}
                onChange={handleTabChange}
                tabBarStyle={{ marginBottom: 0 }}
                onEdit={handleTabEdit}
                destroyInactiveTabPane={true}
                items={sortedComponents.map(component => {
                    const componentPatterns = componentIdToPatterns.get(component.id) || []
                    const filteredPatterns = getFilteredPatterns(componentPatterns)
                    return {
                        key: String(component.id),
                        closable: componentPatterns.length === 0,
                        label: (
                            <span className="component-tab-label-wrapper" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <Text
                                    className="component-tab-title"
                                    ellipsis={{ tooltip: false }}
                                    title={component.name}
                                    onClick={(e) => handleOpenRenameModal(component, e)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {component.name}
                                </Text>
                                <Text type='secondary'>({componentPatterns.length})</Text>
                            </span>
                        ),
                        children: (
                            <Card 
                                bordered={false} 
                                size='small' 
                                style={{ borderTopLeftRadius: 0}} 
                                bodyStyle={{ height: `calc(100vh - 240px)`, overflow: 'hidden' }}
                                className="component-tabs-card"
                            >
                            <Row
                                gutter={0}
                                style={{ height: '100%' }}
                            >
                                <Col
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    lg={6}
                                    xl={5}
                                    xxl={4}
                                    style={{ 
                                        overflow: 'auto', 
                                        borderRight: '1px solid #f0f0f0'
                                    }}
                                    className="pattern-navigation-col"
                                >
                                    <PatternNavigationMenu
                                        patterns={filteredPatterns}
                                        selectedPatternIds={selectedPatternIds}
                                        activePatternId={activePatternId}
                                        onPatternSelect={onPatternSelectChange}
                                        onPatternClick={handlePatternClick}
                                        onPatternMove={onPatternMove}
                                        onPatternDelete={handleRemovePatternAndUpdateActive}
                                        availableComponents={sortedComponents}
                                        currentComponentId={component.id}
                                        form={form}
                                    />
                                </Col>
                                <Col 
                                    xs={24}
                                    sm={24}
                                    md={24}
                                    lg={18}
                                    xl={19}
                                    xxl={20}
                                    style={{ overflow: 'auto' }}
                                    className="pattern-detail-col"
                                >
                                    <PatternDetailView
                                        pattern={filteredPatterns.find(p => p.id === activePatternId)}
                                        form={form}
                                        criteriaUpdating={criteriaUpdating}
                                        updateCriteriaFields={updateCriteriaFields}
                                        onRegenerateAndProject={onRegenerateAndProject}
                                    />
                                </Col>
                            </Row>
                            </Card>
                        )
                    }
                })}
            />

            {isCreateModalOpen && (
                <Modal
                    title="Create New Component"
                    open
                    onCancel={() => setIsCreateModalOpen(false)}
                    confirmLoading={isCreatingComponent}
                    okText="Create"
                    cancelText="Cancel"
                    onOk={() => createForm.submit()}
                >
                    <Form
                        form={createForm}
                        onFinish={handleCreateComponent}
                        layout="vertical"
                        autoComplete="off"
                        requiredMark={false}
                    >
                        <Form.Item
                            name="componentName"
                            label="Name"
                            rules={[
                                { required: true, message: 'Please enter a component name' },
                                { max: COMPONENT_NAME_LIMIT, message: `Component name cannot exceed ${COMPONENT_NAME_LIMIT} characters` }
                            ]}
                        >
                            <Input
                                placeholder="Enter component name"
                                maxLength={COMPONENT_NAME_LIMIT}
                                showCount
                                autoFocus
                                onPressEnter={() => createForm.submit()}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            )}

            <RenameComponentModal
                open={!!componentToRename}
                component={componentToRename}
                existingComponents={sortedComponents}
                onRename={onRenameComponent}
                onCancel={handleCloseRenameModal}
            />
        </>
    )
}

export default ComponentsTabs


